import type {
  DungeonBlueprint,
  DungeonMarker,
  DungeonRegion,
} from './sim/dungeon';
import type { TilePoint } from './sim/mobile';

export type DungeonAutomapMarkerKind = 'entrance' | 'exit' | 'landmark';

export interface DungeonAutomapMarker extends DungeonMarker {
  kind: DungeonAutomapMarkerKind;
}

export interface DungeonAutomapSnapshot {
  rooms: string[];
  connections: string[];
  regions: DungeonRegion[];
  markers: DungeonAutomapMarker[];
  player?: TilePoint;
}

function contains(region: DungeonRegion, point: TilePoint): boolean {
  return (
    point.x >= region.x &&
    point.y >= region.y &&
    point.x < region.x + region.width &&
    point.y < region.y + region.height
  );
}

function roomRevealRegions(
  room: DungeonBlueprint['rooms'][number],
): readonly DungeonRegion[] {
  return room.revealRegions ?? [room.revealRegion];
}

function connectionRevealRegions(
  connection: DungeonBlueprint['connections'][number],
): readonly DungeonRegion[] {
  return connection.revealRegions ?? [connection.revealRegion];
}

/**
 * Run-local fog-of-war state over a semantic dungeon blueprint.
 *
 * The module never inspects art or collision cells. Callers report the player's
 * authoritative tile, and it permanently records only the room and corridor
 * reveal regions that contain that tile. The small interface keeps all branch-
 * concealment and marker-discovery rules in one place for both DOM and tests.
 */
export class DungeonAutomap {
  private readonly revealedRooms = new Set<string>();
  private readonly revealedConnectionRegions = new Set<string>();
  private player: TilePoint | undefined;

  constructor(private readonly blueprint: DungeonBlueprint) {}

  enter(point: TilePoint): boolean {
    this.player = { ...point };
    let changed = false;
    for (const room of this.blueprint.rooms) {
      if (
        !roomRevealRegions(room).some((region) => contains(region, point)) ||
        this.revealedRooms.has(room.id)
      ) {
        continue;
      }
      this.revealedRooms.add(room.id);
      changed = true;
    }
    for (const connection of this.blueprint.connections) {
      connectionRevealRegions(connection).forEach((region, index) => {
        const id = `${connection.id}:${index}`;
        if (!contains(region, point) || this.revealedConnectionRegions.has(id)) return;
        this.revealedConnectionRegions.add(id);
        changed = true;
      });
    }
    return changed;
  }

  snapshot(): DungeonAutomapSnapshot {
    const rooms = this.blueprint.rooms
      .filter((room) => this.revealedRooms.has(room.id))
      .map((room) => room.id);
    const connections = this.blueprint.connections
      .filter((connection) => connectionRevealRegions(connection).some((_, index) =>
        this.revealedConnectionRegions.has(`${connection.id}:${index}`)
      ))
      .map((connection) => connection.id);
    const regions = [
      ...this.blueprint.rooms
        .filter((room) => this.revealedRooms.has(room.id))
        .flatMap((room) => roomRevealRegions(room).map((region) => ({ ...region }))),
      ...this.blueprint.connections
        .flatMap((connection) => connectionRevealRegions(connection)
          .filter((_, index) => this.revealedConnectionRegions.has(`${connection.id}:${index}`))
          .map((region) => ({ ...region }))),
    ];
    const markers: DungeonAutomapMarker[] = [];
    if (this.revealedRooms.has(this.blueprint.entrance.roomId)) {
      markers.push({ kind: 'entrance', ...this.blueprint.entrance });
    }
    if (
      this.blueprint.showExitOnAutomap !== false &&
      this.revealedRooms.has(this.blueprint.exit.roomId)
    ) {
      markers.push({ kind: 'exit', ...this.blueprint.exit });
    }
    for (const landmark of this.blueprint.landmarks) {
      if (this.revealedRooms.has(landmark.roomId)) {
        markers.push({ kind: 'landmark', ...landmark });
      }
    }
    return {
      rooms,
      connections,
      regions,
      markers,
      ...(this.player ? { player: { ...this.player } } : {}),
    };
  }
}
